import { Injectable } from '@angular/core';
import { UserMasterProxyService } from '../proxies/user-master/user-master.proxy.service';
import { jwtDecode } from 'jwt-decode';
import { catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { LoginBody } from '../models/user-master/commands/login-body';
import { Router } from '@angular/router';
import { JwtPayload } from '@card-software/nexus-models';
import {
  AuthService,
  MasterDataService,
  RolesBootstrapService,
  SharedTokenService,
  SiteBootstrapService,
} from '@card-software/nexus-utilities';
import { environment } from '../../environments/environment';
import { EnumService } from './enum.service';
import _ from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class UserAuthenticationService {
  private readonly tokenKey = 'SierraToken';
  private readonly sitesKey = 'SierraSites';
  constructor(
    private userMasterProxyService: UserMasterProxyService,
    private router: Router,
    private enumService: EnumService,
    private siteBootstrapService: SiteBootstrapService,
    private rolesBootstrapService: RolesBootstrapService,
  ) {}

  public login(loginBody: LoginBody): Observable<boolean> {
    return this.userMasterProxyService.login(loginBody).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this.saveToken(response.data);
          this.initializeService();
          this.siteBootstrapService.reset();
          this.rolesBootstrapService.clearCache();
          this.siteBootstrapService.initializeSites(response.data);
          return of(true);
        } else {
          return of(false);
        }
      }),
      switchMap(() =>
        forkJoin({
          enums: this.enumService.loadEnums(),
          rolesBySite: this.rolesBootstrapService.fetchRolesBySite(),
        }),
      ),
      map(() => true),
      catchError((error) => {
        console.error('Error logging in', error);
        return of(false);
      }),
    );
  }

  public logout() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.sitesKey);
    SharedTokenService.clearToken();
    this.siteBootstrapService.reset();
    this.router.navigate(['/login']);
  }

  private initializeService() {
    const token = this.getStoredJwtToken();
    if (_.isNil(token)) {
      console.warn('Token not found');
      this.logout();
    }

    MasterDataService.initialize(environment.proxyUrls.itemApi, token!);
    this.rolesBootstrapService.initializeRoles(token);
  }

  public get userLite() {
    const token = this.getStoredJwtToken();
    if (!token) {
      return null;
    }

    // Decode the token and map to user details
    const { FirstName, LastName, Email, Username }: JwtPayload = jwtDecode<JwtPayload>(token);
    return { firstName: FirstName, lastName: LastName, email: Email, username: Username };
  }

  /**
   * Global roles (union across every site) — no longer travel in the JWT, this
   * reads the set already populated by RolesBootstrapService.fetchRolesBySite().
   */
  public get roles(): string[] {
    return AuthService.listGlobalRoles();
  }

  public get isUserAuthenticated(): boolean {
    const token = this.getStoredJwtToken();
    if (!token) {
      return false;
    }
    const isTokenExpired = this.isTokenExpired(token);
    if (isTokenExpired) {
      this.logout();
      return false;
    }
    return true;
  }
  public validateTokenAndAuthenticate(token: string): boolean {
    if (!token || this.isTokenExpired(token)) {
      console.warn('Token is invalid or expired');
      return false;
    }

    this.saveToken(token);
    return true;
  }

  private saveToken(token: string) {
    localStorage.setItem(this.tokenKey, token);
    SharedTokenService.saveToken(token);
    this.initializeService();
  }
  getStoredJwtToken() {
    return localStorage.getItem(this.tokenKey);
  }

  isTokenExpired(token: string) {
    try {
      const { exp }: { exp: number } = jwtDecode(token);
      return exp * 1000 < Date.now();
    } catch (e) {
      console.error('Error decoding token', e);
      return true;
    }
  }
}
